import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, shareReplay } from 'rxjs';
import { environment } from 'src/environments/environment';
const DEFAULT_DOMAIN = '';
const STATIC_FEEDBACK_JWT =
  '';
export interface FeedbackQuestion {
  questionId: number;
  question: string;
  teluguQuestion?: string;
  questionType?: string;
  questionTitle?: string;
}

export interface FeedbackOption {
  optionId: number;
  optionName: string;
  teluguOptionName?: string;
  optionRank: number;
  optionRankStatus?: boolean;
}

export interface PreProgramQuestionAndOption {
  questionId: number;
  optionId: number;
  feedbackOptionText: string;
  feedbackId: number;
  feedbackPopUpText: string;
}

export interface SavePreProgramDetailsPayload {
  payrollId: string;
  questionAndOption: PreProgramQuestionAndOption[];
  feedBackType: string;
  catagory: string;
  validateMessage: string;
}

const DEFAULT_FEEDBACK_TYPE = 'EMPLOYEE_FEEDBACK';
const STATIC_ENCRYPTED_PAYROLL_ID_ENCODED = '';

export interface GetPreProgramDetailsResponse {
  payrollId?: string;
  category?: string;
  catagory?: string;
  validateMessage?: string;
  questionAndOption?: Array<{
    questionId?: number;
    optionId?: number;
    feedbackOptionText?: string;
    feedbackId?: number;
    feedbackPopUpText?: string;
  }>;
  // Some backends return the list directly.
  data?: unknown;
}

export interface UserProfileResponse {
  basicInfo?: {
    doj?: string;
    designation?: string;
    subject?: string;
    department?: string;
    highestQualification?: string;
    totalExperience?: string;
    monthlyCtc?: string;
    modeOfWork?: string;
    empStatus?: string;
    campusId?: number;
    cityId?: number;
    cityName?: string;
    stateId?: number;
    stateName?: string;
    managerName?: string;
    managerPayrollId?: string;
    [key: string]: unknown;
  };
  personalDetails?: Record<string, unknown>;
  employeeDashBoardInfo?: {
    empName?: string;
    campusName?: string;
    payrollId?: string;
    tempPayrollId?: string;
    employeeId?: number;
    campusId?: number;
    businessType?: string;
    allowedCampusStatus?: boolean;
  };
  managerDetails?: {
    managerName?: string;
    managerPayrollId?: string;
    managerDesignation?: string;
    managerMobile?: string;
    isManager?: boolean;
  };
  currentCampusDetails?: {
    stateId?: number;
    stateName?: string;
    cityId?: number;
    cityName?: string;
    campusId?: number;
    campusName?: string;
  };
}

export interface ScenarioAssessmentOptionDto {
  optionId: number;
  optionText: string;
  optionSequence: string; // "A"|"B"|"C"|"D"
}

export interface ScenarioAssessmentQuestionDto {
  questionId: number;
  questionTitle: string;
  questionText: string;
  questionScenario: string;
  optionsList: ScenarioAssessmentOptionDto[];
}

export interface ScenarioAssessmentSectionDto {
  examId: number;
  sectionId: number;
  sectionName: string;
  questionDetailsList: ScenarioAssessmentQuestionDto[];
}

export interface ScenarioAssessmentAttemptedQuestionPayload {
  questionId: number;
  optionId: number;
  timeTaken: number;
}

export interface ScenarioAssessmentAttemptSectionPayload {
  sectionId: number;
  attemptedQuestions: ScenarioAssessmentAttemptedQuestionPayload[];
}

export interface ScenarioAssessmentSavePayload {
  examId: number;
  attemptStartTime: number;
  attemptendTIme: number;
  sections: ScenarioAssessmentAttemptSectionPayload[];
  attemptedCount: number;
}

@Injectable({
  providedIn: 'root'
})
export class SelfAssessmentService {
  private readonly API_ORIGIN = String((environment as any).masterDomain|| DEFAULT_DOMAIN).trim().replace(/\/+$/, '');
  private readonly questionsUrl = `${this.API_ORIGIN}/employee/api/jwtV2/feedBackQuestions`;
  private readonly optionsUrl = `${this.API_ORIGIN}/employee/api/jwtV2/feedBackOptions`;
  private readonly savePreProgramUrl = `${this.API_ORIGIN}/employee/api/jwtV2/empLeadership/savePreProgramDetails`;
  private readonly getPreProgramUrl = `${this.API_ORIGIN}/employee/api/jwtV2/empLeadership/getPreProgramDetails`;
  private readonly userProfileUrl = `${this.API_ORIGIN}/employee/api/jwtV2/common/userProfile`;
  private readonly scenarioQuestionsUrl = `${this.API_ORIGIN}/employee/api/jwtV2/empFeedBack/pplSelfAssesment/questionAndOptionDetails`;
  private readonly scenarioSaveUrl = `${this.API_ORIGIN}/employee/api/jwtV2/empFeedBack/pplSelfAssesment/saveAttemptDetails`;

  private readonly cachedOptionsByQuestionId = new Map<number, Observable<FeedbackOption[]>>();

  private readonly tokenStorageKeys = ['updated', 'jwtToken', 'token', 'authToken', 'accessToken'] as const;
  private readonly payrollStorageKeys = ['userId'] as const;
  private readonly encryptedPayrollStorageKeys = [
    'uniqueRefId',
  ] as const;

  private get authHeaderValue(): string {
    let raw: string | null = null;
    for (const key of this.tokenStorageKeys) {
      raw = sessionStorage.getItem(key) || localStorage.getItem(key);
      if (raw && raw.trim()) break;
    }
    const trimmed = (raw || '').trim();
    if (trimmed) {
      if (/^Bearer\s+/i.test(trimmed)) return trimmed;
      return `Bearer ${trimmed}`;
    }
    return `Bearer ${STATIC_FEEDBACK_JWT}`;
  }

  private get headers(): HttpHeaders {
    let h = new HttpHeaders({ 'Content-Type': 'application/json' });
    const auth = this.authHeaderValue;
    if (auth) h = h.set('Authorization', auth);
    return h;
  }

  /** GET image — no JSON Content-Type (some servers reject it for binary). */
  private get imageAuthHeaders(): HttpHeaders {
    let h = new HttpHeaders();
    const auth = this.authHeaderValue;
    if (auth) h = h.set('Authorization', auth);
    return h;
  }

  constructor(private readonly http: HttpClient) {}

  getStoredPayrollId(): string {
    for (const key of this.payrollStorageKeys) {
      const v = localStorage.getItem(key) || sessionStorage.getItem(key);
      if (v && String(v).trim()) return String(v).trim();
    }

    return '';
  }

  /** For userProfile API which expects an encrypted payroll id (base64-like). */
  getStoredEncryptedPayrollId(): string | null {
    for (const key of this.encryptedPayrollStorageKeys) {
      const v = localStorage.getItem(key) || sessionStorage.getItem(key);
      if (v && String(v).trim()) return String(v).trim();
    }
    // Static fallback when storage doesn't have encrypted value.
    return STATIC_ENCRYPTED_PAYROLL_ID_ENCODED;
  }

  getQuestions(categoryName: string): Observable<FeedbackQuestion[]> {
    // encodeURIComponent uses %20 for spaces; URLSearchParams uses + which looks odd in URLs
    const q = encodeURIComponent(categoryName.trim());
    return this.http.get<FeedbackQuestion[]>(`${this.questionsUrl}?categoryName=${q}`, { headers: this.headers });
  }

  getOptions(questionId: number): Observable<FeedbackOption[]> {
    const qs = new URLSearchParams();
    qs.set('questionId', String(questionId));
    return this.http.get<FeedbackOption[]>(`${this.optionsUrl}?${qs.toString()}`, { headers: this.headers }).pipe(
      map((opts) =>
        [...(opts ?? [])].sort((a, b) => {
          const ra = Number(a.optionRank ?? 0);
          const rb = Number(b.optionRank ?? 0);
          if (ra !== rb) return ra - rb;
          return Number(a.optionId ?? 0) - Number(b.optionId ?? 0);
        })
      )
    );
  }

  getOptionsCached(questionId: number): Observable<FeedbackOption[]> {
    const id = Number(questionId);
    if (!Number.isFinite(id)) return of([]);
    const existing = this.cachedOptionsByQuestionId.get(id);
    if (existing) return existing;
    const obs = this.getOptions(id).pipe(shareReplay(1));
    this.cachedOptionsByQuestionId.set(id, obs);
    return obs;
  }

  savePreProgramDetails(payload: SavePreProgramDetailsPayload): Observable<unknown> {
    // Ensure feedBackType is always EMPLOYEE_FEEDBACK as required by backend.
    const body: SavePreProgramDetailsPayload = {
      ...payload,
      feedBackType: DEFAULT_FEEDBACK_TYPE
    };
    return this.http.post(this.savePreProgramUrl, body, { headers: this.headers });
  }

  getPreProgramDetails(payrollId: string, category: string): Observable<GetPreProgramDetailsResponse | unknown> {
    const pid = encodeURIComponent(String(payrollId ?? '').trim());
    const cat = encodeURIComponent(String(category ?? '').trim());
    const url = `${this.getPreProgramUrl}?payrollId=${pid}&category=${cat}`;
    return this.http.get(url, { headers: this.headers });
  }

  getUserProfile(payrollId: string): Observable<UserProfileResponse> {
    const raw = String(payrollId ?? '').trim();
    // Accept already-encoded encrypted payroll ids (e.g. FS%2F...%3D) without double-encoding.
    const pid = raw.includes('%') ? raw : encodeURIComponent(raw);
    const url = `${this.userProfileUrl}?payrollId=${pid}`;
    return this.http.get<UserProfileResponse>(url, { headers: this.headers });
  }

  /**
   * Profile image bytes (Bearer required — do not use the URL as a plain img src; fetch via this method).
   * Default: `{origin}/employee/api/jwtV2/userId/profileImage?payrollId=…&tempPayrollId=…`
   * When `preferHighResolution` is true (default), adds `width` / `height` (DPR-aware, capped at 1024) if not already set in `query`, so small thumbnails scale down sharply in the UI.
   * Pass `payrollId` / `tempPayrollId` from `getUserProfile` → `employeeDashBoardInfo` when available.
   * If your API uses the real id in the path instead of the literal `userId`, pass `{ pathSegment: payrollOrUserId }`.
   */
  getProfileImageBlob(options?: {
    pathSegment?: string;
    /** Prefer `employeeDashBoardInfo.payrollId` (fallback: tempPayrollId, then storage). */
    payrollId?: string;
    /** Same profile response — send as `tempPayrollId` query param when present. */
    tempPayrollId?: string;
    query?: Record<string, string>;
    /**
     * When true (default), adds `width` / `height` query hints (DPR-aware, up to 1024px) so the server can return a larger bitmap.
     * Set false if your API rejects unknown params.
     */
    preferHighResolution?: boolean;
  }): Observable<Blob> {
    const rawSeg = (options?.pathSegment ?? 'userId').trim() || 'userId';
    const segment = encodeURIComponent(rawSeg);
    const qs = new URLSearchParams();
    const temp = String(options?.tempPayrollId ?? '').trim();
    const primary =
      String(options?.payrollId ?? '').trim() || temp || this.getStoredPayrollId();
    if (primary) qs.set('payrollId', primary);
    if (temp) qs.set('tempPayrollId', temp);
    if (options?.query) {
      for (const [k, v] of Object.entries(options.query)) {
        if (v != null && String(v).trim()) qs.set(k, String(v));
      }
    }
    const preferHi = options?.preferHighResolution !== false;
    if (preferHi) {
      const maxPx = 1024;
      const basePerDpr = 320;
      let px = 512;
      try {
        const dpr = Math.min(3, Math.max(1, typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1));
        px = Math.min(maxPx, Math.round(basePerDpr * dpr));
      } catch {
        px = 512;
      }
      if (!qs.has('width')) qs.set('width', String(px));
      if (!qs.has('height')) qs.set('height', String(px));
    }
    const queryString = qs.toString();
    const base = `${this.API_ORIGIN}/employee/api/jwtV2/${segment}/profileImage`;
    const url = queryString ? `${base}?${queryString}` : base;
    const imgHeaders = this.imageAuthHeaders.set(
      'Accept',
      'image/avif,image/webp,image/apng,image/jpeg,image/png,image/*,*/*;q=0.8'
    );
    return this.http.get(url, { headers: imgHeaders, responseType: 'blob' }).pipe(
      catchError(() => of(new Blob()))
    );
  }

  /** Scenario-based assessment: section-wise questions with options (A/B/C/D). */
  getScenarioAssessmentQuestionAndOptionDetails(): Observable<ScenarioAssessmentSectionDto[]> {
    return this.http.get<ScenarioAssessmentSectionDto[]>(this.scenarioQuestionsUrl, { headers: this.headers }).pipe(
      map((res) => (Array.isArray(res) ? res : []))
    );
  }

  saveScenarioAssessmentAttempt(payload: ScenarioAssessmentSavePayload): Observable<unknown> {
    return this.http.post(this.scenarioSaveUrl, payload, { headers: this.headers });
  }
}
